import { type FC } from 'react';
import type { SimulationTurn } from '@/types/simulation';

interface ImpactMetersProps {
  turn: SimulationTurn;
}

const ProgressBar: FC<{ value: number; max?: number; colorClass: string }> = ({
  value, max = 100, colorClass
}) => (
  <div className="w-full bg-gray-900 rounded-full h-1.5 overflow-hidden">
    <div
      className={`h-full rounded-full transition-all duration-700 ${colorClass}`}
      style={{ width: `${Math.min(100, (value / max) * 100)}%` }}
    />
  </div>
);

export const ImpactMeters: FC<ImpactMetersProps> = ({ turn }) => {
  const { impacts, leader_assessments, country_statuses, resolution_probability } = turn;

  const gdpColor = impacts.global_gdp_pct >= 0 ? 'text-emerald-400' : 'text-red-400';
  const nuclearColor = impacts.nuclear_risk >= 70 ? 'text-red-500 animate-pulse' :
    impacts.nuclear_risk >= 40 ? 'text-orange-400' : 'text-yellow-400';
  const resolutionColor = resolution_probability >= 60 ? 'text-emerald-400' :
    resolution_probability >= 30 ? 'text-yellow-400' : 'text-red-400';

  return (
    <div className="space-y-2.5">
      {/* GDP Impact */}
      <div className="bg-gray-900/60 border border-slate-800/60 rounded-lg px-3 py-2.5">
        <div className="flex items-center justify-between mb-1.5">
          <span className="text-[10px] text-slate-500 uppercase tracking-wider font-semibold">
            📉 Global GDP Impact
          </span>
          <span className={`text-sm font-bold tabular-nums ${gdpColor}`}>
            {impacts.global_gdp_pct >= 0 ? '+' : ''}{impacts.global_gdp_pct.toFixed(1)}%
          </span>
        </div>
        {/* Per-country bar chart */}
        {impacts.country_gdp.slice(0, 6).length > 0 && (() => {
          const maxAbs = Math.max(...impacts.country_gdp.slice(0, 6).map((c) => Math.abs(c.pct)), 1);
          return (
            <div className="mt-2 space-y-1">
              {impacts.country_gdp.slice(0, 6).map((c) => (
                <div key={c.code} className="flex items-center gap-1.5">
                  <span className="text-[9px] text-slate-500 w-7 shrink-0 text-right">{c.code}</span>
                  <div className="flex-1 flex items-center h-3 relative">
                    {/* Center line */}
                    <div className="absolute left-1/2 top-0 bottom-0 w-px bg-slate-700/60" />
                    {c.pct < 0 ? (
                      /* Negative bar grows left from center */
                      <div className="absolute right-1/2 top-0.5 bottom-0.5 rounded-sm bg-red-600/70"
                        style={{ width: `${(Math.abs(c.pct) / maxAbs) * 50}%` }} />
                    ) : (
                      /* Positive bar grows right from center */
                      <div className="absolute left-1/2 top-0.5 bottom-0.5 rounded-sm bg-emerald-600/70"
                        style={{ width: `${(c.pct / maxAbs) * 50}%` }} />
                    )}
                  </div>
                  <span className={`text-[9px] tabular-nums w-10 shrink-0 ${c.pct >= 0 ? 'text-emerald-500' : 'text-red-500'}`}>
                    {c.pct >= 0 ? '+' : ''}{c.pct.toFixed(1)}%
                  </span>
                </div>
              ))}
            </div>
          );
        })()}
      </div>

      {/* Nuclear Risk */}
      <div className="bg-gray-900/60 border border-slate-800/60 rounded-lg px-3 py-2.5">
        <div className="flex items-center justify-between mb-1.5">
          <span className="text-[10px] text-slate-500 uppercase tracking-wider font-semibold">
            ☢ Nuclear Risk
          </span>
          <span className={`text-sm font-bold tabular-nums ${nuclearColor}`}>
            {impacts.nuclear_risk}%
          </span>
        </div>
        <ProgressBar
          value={impacts.nuclear_risk}
          colorClass={impacts.nuclear_risk >= 70 ? 'bg-red-500' : impacts.nuclear_risk >= 40 ? 'bg-orange-500' : 'bg-yellow-600'}
        />
      </div>

      {/* Casualties + Involved */}
      <div className="grid grid-cols-2 gap-2">
        <div className="bg-gray-900/60 border border-slate-800/60 rounded-lg px-3 py-2.5">
          <span className="text-[10px] text-slate-500 uppercase tracking-wider font-semibold block mb-1">
            💀 Casualties
          </span>
          <span className="text-xs text-slate-300 font-medium leading-tight">
            {impacts.casualties}
          </span>
        </div>
        <div className="bg-gray-900/60 border border-slate-800/60 rounded-lg px-3 py-2.5">
          <span className="text-[10px] text-slate-500 uppercase tracking-wider font-semibold block mb-1">
            🌍 Involved
          </span>
          <span className="text-sm font-bold text-slate-200">
            {impacts.countries_involved.length}
            <span className="text-xs font-normal text-slate-500 ml-1">nations</span>
          </span>
        </div>
      </div>

      {/* Market */}
      {impacts.market_snapshot && (
        <div className="bg-gray-900/60 border border-slate-800/60 rounded-lg px-3 py-2">
          <span className="text-[10px] text-slate-500 uppercase tracking-wider font-semibold block mb-1">
            💹 Market Snapshot
          </span>
          <p className="text-[11px] text-slate-400 leading-snug">{impacts.market_snapshot}</p>
        </div>
      )}

      {/* Supply chain */}
      {impacts.supply_chain_hit && impacts.supply_chain_hit.length > 0 && (
        <div className="bg-gray-900/60 border border-slate-800/60 rounded-lg px-3 py-2">
          <span className="text-[10px] text-slate-500 uppercase tracking-wider font-semibold block mb-1.5">
            🔗 Supply Chain Impact
          </span>
          <div className="flex flex-wrap gap-1">
            {impacts.supply_chain_hit.map((s) => (
              <span key={s} className="text-[10px] bg-orange-950/60 text-orange-400 border border-orange-800/40 rounded px-1.5 py-0.5">
                {s}
              </span>
            ))}
          </div>
        </div>
      )}

      {/* Resolution probability */}
      <div className="bg-gray-900/60 border border-slate-800/60 rounded-lg px-3 py-2.5">
        <div className="flex items-center justify-between mb-1.5">
          <span className="text-[10px] text-slate-500 uppercase tracking-wider font-semibold">
            🕊 Resolution Probability
          </span>
          <span className={`text-sm font-bold tabular-nums ${resolutionColor}`}>
            {resolution_probability}%
          </span>
        </div>
        <ProgressBar
          value={resolution_probability}
          colorClass={resolution_probability >= 60 ? 'bg-emerald-500' : resolution_probability >= 30 ? 'bg-yellow-600' : 'bg-red-600'}
        />
      </div>

      {/* Leader assessments */}
      {leader_assessments.length > 0 && (
        <div className="bg-gray-900/60 border border-slate-800/60 rounded-lg px-3 py-2.5">
          <span className="text-[10px] text-slate-500 uppercase tracking-wider font-semibold block mb-2">
            👤 Leader Analysis
          </span>
          <div className="space-y-1.5">
            {leader_assessments.map((la, i) => (
              <div key={i} className="text-[11px]">
                <span className="text-amber-400 font-semibold">{la.leader}: </span>
                <span className="text-slate-400">{la.assessment}</span>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Country statuses */}
      {country_statuses.length > 0 && (
        <div className="bg-gray-900/60 border border-slate-800/60 rounded-lg px-3 py-2.5">
          <span className="text-[10px] text-slate-500 uppercase tracking-wider font-semibold block mb-2">
            🚨 Country Status
          </span>
          <div className="space-y-1.5">
            {country_statuses.map((cs, i) => (
              <div key={i} className="flex items-start gap-2 text-[11px]">
                <span className="text-slate-200 font-bold min-w-[28px]">{cs.code}</span>
                <div>
                  <span className="text-red-400 font-medium">[{cs.alert}] </span>
                  <span className="text-slate-400">{cs.leader_action}</span>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};
